// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import EmojiPicker from '@app/features/emoji/state/EmojiPicker';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import {GuildEmoji} from '@app/features/expressions/models/GuildEmoji';
import {patchGuildEmojiCacheFromGateway} from '@app/features/expressions/state/GuildExpressionTabCache';
import {filterEmojisForAutocomplete} from '@app/features/expressions/utils/ExpressionPermissionUtils';
import UnicodeEmojis from '@app/features/expressions/utils/UnicodeEmojis';
import type {GuildReadyData} from '@app/features/gateway/types/GatewayGuildTypes';
import GuildList from '@app/features/guild/state/GuildList';
import * as RegexUtils from '@app/features/messaging/utils/RegexUtils';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import type {GuildEmoji as WireGuildEmoji} from '@app/vendor/schema/domains/guild/GuildEmojiSchemas';
import type {Guild as WireGuild} from '@app/vendor/schema/domains/guild/GuildResponseSchemas';
import {EmojiStateSchema} from '@app/vendor/schema/gen/fluxer/user/preferences/v1/pickers_pb';
import {sortBySnowflakeDesc} from '@app/vendor/snowflake/SnowflakeUtils';
import {i18n} from '@lingui/core';
import {makeAutoObservable} from 'mobx';

type GuildEmojiContext = Readonly<{
	emojis: ReadonlyArray<GuildEmoji>;
	usableEmojis: ReadonlyArray<GuildEmoji>;
}>;
export function normalizeEmojiSearchQuery(query: string): string {
	return query['trim']().replace(/^:+/, '').replace(/:+$/, '');
}
type EmojiSearchRegexes = {
	contains: RegExp;
	startsWith: RegExp;
	boundary: RegExp;
};
const EMOJI_REGEX_CACHE_LIMIT = 16;
const emojiSearchRegexCache = new Map<string, EmojiSearchRegexes>();
function getEmojiSearchRegexes(lowerCasedQuery: string): EmojiSearchRegexes {
	const cached = emojiSearchRegexCache.get(lowerCasedQuery);
	if (cached) {
		emojiSearchRegexCache.delete(lowerCasedQuery);
		emojiSearchRegexCache.set(lowerCasedQuery, cached);
		return cached;
	}
	const escapedQuery = RegexUtils.escapeRegex(lowerCasedQuery);
	const regexes: EmojiSearchRegexes = {
		contains: new RegExp(escapedQuery, 'i'),
		startsWith: new RegExp(`^${escapedQuery}`, 'i'),
		boundary: new RegExp(`(^|_|[A-Z])${escapedQuery}s?([A-Z]|_|$)`),
	};
	emojiSearchRegexCache.set(lowerCasedQuery, regexes);
	if (emojiSearchRegexCache.size > EMOJI_REGEX_CACHE_LIMIT) {
		const firstKey = emojiSearchRegexCache.keys().next().value;
		if (firstKey !== undefined) emojiSearchRegexCache.delete(firstKey);
	}
	return regexes;
}
class EmojiDisambiguations {
	private static _lastInstance: EmojiDisambiguations | null = null;
	private readonly guildId: string | null;
	private disambiguatedEmoji: ReadonlyArray<FlatEmoji> | null = null;
	private customEmojis: ReadonlyMap<string, FlatEmoji> | null = null;
	private emojisByName: ReadonlyMap<string, FlatEmoji> | null = null;
	private emojisById: ReadonlyMap<string, FlatEmoji> | null = null;
	private constructor(guildId?: string | null) {
		this.guildId = guildId ?? null;
	}
	static getInstance(guildId?: string | null): EmojiDisambiguations {
		if (!EmojiDisambiguations._lastInstance || EmojiDisambiguations._lastInstance.guildId !== guildId) {
			EmojiDisambiguations._lastInstance = new EmojiDisambiguations(guildId);
		}
		return EmojiDisambiguations._lastInstance;
	}
	static reset(): void {
		EmojiDisambiguations._lastInstance = null;
	}
	static clear(guildId?: string | null): void {
		if (EmojiDisambiguations._lastInstance?.guildId === guildId) {
			EmojiDisambiguations._lastInstance = null;
		}
	}
	getDisambiguatedEmoji(): ReadonlyArray<FlatEmoji> {
		this.ensureDisambiguated();
		return this.disambiguatedEmoji ?? [];
	}
	getCustomEmoji(): ReadonlyMap<string, FlatEmoji> {
		this.ensureDisambiguated();
		return this.customEmojis ?? new Map();
	}
	getByName(disambiguatedEmojiName: string): FlatEmoji | undefined {
		this.ensureDisambiguated();
		return this.emojisByName?.get(disambiguatedEmojiName);
	}
	getById(emojiId: string): FlatEmoji | undefined {
		this.ensureDisambiguated();
		return this.emojisById?.get(emojiId);
	}
	nameMatchesChain(testName: (name: string) => boolean): ReadonlyArray<FlatEmoji> {
		return this.getDisambiguatedEmoji().filter(({names, name}) => (names ? names.some(testName) : testName(name)));
	}
	private ensureDisambiguated(): void {
		if (!this.disambiguatedEmoji) {
			const result = this.buildDisambiguatedCustomEmoji();
			this.disambiguatedEmoji = result.disambiguatedEmoji;
			this.customEmojis = result.customEmojis;
			this.emojisByName = result.emojisByName;
			this.emojisById = result.emojisById;
		}
	}
	private buildDisambiguatedCustomEmoji() {
		const emojiCountByName = new Map<string, number>();
		const disambiguatedEmoji: Array<FlatEmoji> = [];
		const customEmojis = new Map<string, FlatEmoji>();
		const emojisByName = new Map<string, FlatEmoji>();
		const emojisById = new Map<string, FlatEmoji>();
		const disambiguateEmoji = (emoji: FlatEmoji): void => {
			const uniqueName = emoji.name;
			const existingCount = emojiCountByName.get(uniqueName) ?? 0;
			emojiCountByName.set(uniqueName, existingCount + 1);
			const finalEmoji =
				existingCount > 0
					? {
							...emoji,
							name: `${uniqueName}~${existingCount}`,
							uniqueName,
							allNamesString: `:${uniqueName}~${existingCount}:`,
						}
					: emoji;
			emojisByName.set(finalEmoji.name, finalEmoji);
			if (finalEmoji.id) {
				emojisById.set(finalEmoji.id, finalEmoji);
				customEmojis.set(finalEmoji.name, finalEmoji);
			}
			disambiguatedEmoji.push(finalEmoji);
		};
		UnicodeEmojis.forEachEmoji((unicodeEmoji) => {
			const compatibleEmoji: FlatEmoji = {
				...unicodeEmoji,
				name: unicodeEmoji.uniqueName,
				url: unicodeEmoji.url || undefined,
				useSpriteSheet: unicodeEmoji.useSpriteSheet,
				index: unicodeEmoji.index,
				diversityIndex: unicodeEmoji.diversityIndex,
				hasDiversity: unicodeEmoji.hasDiversity,
			};
			disambiguateEmoji(compatibleEmoji);
		});
		const processGuildEmojis = (guildId: string) => {
			const guildEmoji = emojiGuildRegistry.get(guildId);
			if (!guildEmoji) return;
			guildEmoji.usableEmojis.forEach((emoji) => {
				const emojiForDisambiguation: FlatEmoji = {
					...emoji,
					name: emoji.name,
					uniqueName: emoji.name,
					allNamesString: emoji.allNamesString,
					url: emoji.url,
					useSpriteSheet: false,
				};
				disambiguateEmoji(emojiForDisambiguation);
			});
		};
		if (this.guildId) {
			processGuildEmojis(this.guildId);
		}
		for (const guild of GuildList.guilds.filter((guild) => guild.id !== this.guildId)) {
			processGuildEmojis(guild.id);
		}
		return {
			disambiguatedEmoji: Object.freeze(disambiguatedEmoji),
			customEmojis: new Map(customEmojis),
			emojisByName: new Map(emojisByName),
			emojisById: new Map(emojisById),
		};
	}
}
class EmojiGuildRegistry {
	private guilds = new Map<string, GuildEmojiContext>();
	private customEmojisById = new Map<string, GuildEmoji>();
	private emojiIdsByGuild = new Map<string, Set<string>>();
	reset(): void {
		this.guilds.clear();
		this.customEmojisById.clear();
		this.emojiIdsByGuild.clear();
		EmojiDisambiguations.reset();
	}
	deleteGuild(guildId: string): void {
		this.guilds.delete(guildId);
		const previousIds = this.emojiIdsByGuild.get(guildId);
		if (previousIds) {
			for (const emojiId of previousIds) {
				this.customEmojisById.delete(emojiId);
			}
			this.emojiIdsByGuild.delete(guildId);
		}
	}
	get(guildId: string): GuildEmojiContext | undefined {
		return this.guilds.get(guildId);
	}
	rebuildRegistry(): void {
		this.customEmojisById.clear();
		this.emojiIdsByGuild.clear();
		for (const [guildId, guild] of this.guilds.entries()) {
			const ids = new Set<string>();
			for (const emoji of guild.usableEmojis) {
				this.customEmojisById.set(emoji.id, emoji);
				ids.add(emoji.id);
			}
			this.emojiIdsByGuild.set(guildId, ids);
		}
		EmojiDisambiguations.reset();
	}
	updateGuild(guildId: string, guildEmojis?: ReadonlyArray<WireGuildEmoji>): void {
		this.deleteGuild(guildId);
		EmojiDisambiguations.clear(guildId);
		if (!guildEmojis) return;
		const emojiRecords = guildEmojis.map((emoji) => new GuildEmoji(guildId, emoji));
		const sortedEmojis = sortBySnowflakeDesc(emojiRecords);
		const frozenEmojis = Object.freeze(sortedEmojis);
		this.guilds.set(guildId, {
			emojis: frozenEmojis,
			usableEmojis: frozenEmojis,
		});
		const ids = new Set<string>();
		for (const emoji of frozenEmojis) {
			this.customEmojisById.set(emoji.id, emoji);
			ids.add(emoji.id);
		}
		this.emojiIdsByGuild.set(guildId, ids);
	}
	getGuildEmojis(guildId: string): ReadonlyArray<GuildEmoji> {
		return this.guilds.get(guildId)?.usableEmojis ?? [];
	}
}
const emojiGuildRegistry = new EmojiGuildRegistry();
class Emoji {
	skinTone = '';
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.initPersistence();
	}
	private async initPersistence(): Promise<void> {
		await makeSyncedField(this, {
			field: 'emoji',
			schema: EmojiStateSchema,
			persist: ['skinTone'],
			toMessage: (s) => ({skinTone: s.skinTone}),
			applyMessage: (s, m) => {
				s.skinTone = m.skinTone;
			},
		});
		UnicodeEmojis.setDefaultSkinTone(this.skinTone);
	}
	get categories(): ReadonlyArray<string> {
		return Object.freeze(['custom', ...UnicodeEmojis.getCategories()]);
	}
	getGuildEmoji(guildId: string): ReadonlyArray<GuildEmoji> {
		return emojiGuildRegistry.getGuildEmojis(guildId);
	}
	getEmojiById(emojiId: string): FlatEmoji | undefined {
		return this.getDisambiguatedEmojiContext(null).getById(emojiId);
	}
	getDisambiguatedEmojiContext(guildId?: string | null): EmojiDisambiguations {
		return EmojiDisambiguations.getInstance(guildId);
	}
	getEmojiMarkdown(emoji: FlatEmoji): string {
		if (emoji.id) {
			return `<${emoji.animated ? 'a' : ''}:${emoji.uniqueName}:${emoji.id}>`;
		}
		if (emoji.hasDiversity && this.skinTone) {
			const skinToneName = UnicodeEmojis.convertSurrogateToName(this.skinTone, false);
			if (skinToneName) {
				return `:${emoji.uniqueName}::${skinToneName}:`;
			}
		}
		return `:${emoji.uniqueName}:`;
	}
	filterExternal(
		channel: Channel | null,
		nameTest: (name: string) => boolean,
		count: number,
	): ReadonlyArray<FlatEmoji> {
		const results = EmojiDisambiguations.getInstance(channel?.guildId).nameMatchesChain(nameTest);
		const filtered = filterEmojisForAutocomplete(i18n, results, channel);
		return count > 0 ? filtered.slice(0, count) : filtered;
	}
	getAllEmojis(channel: Channel | null): ReadonlyArray<FlatEmoji> {
		return this.getDisambiguatedEmojiContext(channel?.guildId).getDisambiguatedEmoji();
	}
	search(channel: Channel | null, query: string, count = 0): ReadonlyArray<FlatEmoji> {
		const normalizedQuery = normalizeEmojiSearchQuery(query);
		const lowerCasedQuery = normalizedQuery.toLowerCase();
		if (!lowerCasedQuery) {
			const allEmojis = this.getAllEmojis(channel);
			const sorted = [...allEmojis].sort(
				(a, b) => EmojiPicker.getFrecencyScoreForEmoji(b) - EmojiPicker.getFrecencyScoreForEmoji(a),
			);
			return count > 0 ? sorted.slice(0, count) : sorted;
		}
		const {
			contains: containsRegex,
			startsWith: startsWithRegex,
			boundary: boundaryRegex,
		} = getEmojiSearchRegexes(lowerCasedQuery);
		const searchResults = this.filterExternal(channel, containsRegex.test.bind(containsRegex), 0);
		if (searchResults.length === 0) return searchResults;
		const getScore = (name: string): number => {
			const nameLower = name.toLowerCase();
			return (
				1 +
				(nameLower === lowerCasedQuery ? 4 : 0) +
				(boundaryRegex.test(nameLower) || boundaryRegex.test(name) ? 2 : 0) +
				(startsWithRegex.test(name) ? 1 : 0)
			);
		};
		const sortedResults = [...searchResults].sort((a, b) => {
			const frecencyDiff = EmojiPicker.getFrecencyScoreForEmoji(b) - EmojiPicker.getFrecencyScoreForEmoji(a);
			if (frecencyDiff !== 0) {
				return frecencyDiff;
			}
			const aName = a.names?.[0] ?? a.name;
			const bName = b.names?.[0] ?? b.name;
			const scoreDiff = getScore(bName) - getScore(aName);
			return scoreDiff || aName.localeCompare(bName);
		});
		return count > 0 ? sortedResults.slice(0, count) : sortedResults;
	}
	setSkinTone(skinTone: string): void {
		this.skinTone = skinTone;
		UnicodeEmojis.setDefaultSkinTone(skinTone);
	}
	handleConnectionOpen({guilds}: {guilds: ReadonlyArray<GuildReadyData>}): void {
		emojiGuildRegistry.reset();
		for (const guild of guilds) {
			emojiGuildRegistry.updateGuild(guild.id, guild.emojis);
		}
		ComponentDispatch.dispatch('EMOJI_PICKER_RERENDER');
	}
	handleGuildUpdate({guild}: {guild: GuildReadyData | WireGuild}): void {
		if (!('emojis' in guild)) {
			ComponentDispatch.dispatch('EMOJI_PICKER_RERENDER');
			return;
		}
		emojiGuildRegistry.updateGuild(guild.id, guild.emojis);
		ComponentDispatch.dispatch('EMOJI_PICKER_RERENDER');
	}
	handleGuildEmojiUpdated({guildId, emojis}: {guildId: string; emojis: ReadonlyArray<WireGuildEmoji>}): void {
		emojiGuildRegistry.updateGuild(guildId, emojis);
		patchGuildEmojiCacheFromGateway(guildId, emojis);
		ComponentDispatch.dispatch('EMOJI_PICKER_RERENDER');
	}
	handleGuildDelete({guildId}: {guildId: string}): void {
		emojiGuildRegistry.deleteGuild(guildId);
		ComponentDispatch.dispatch('EMOJI_PICKER_RERENDER');
	}
}
export default new Emoji();
